// Градове и райони — `cities` and `city_areas` (plan §2.2, §4.2).
//
// Polygons are not edited here: the generator needs names, and `leads.area` falls back to the
// alias match when a polygon is missing. The editor only reports whether one exists.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../../lib/supabase.js';
import { MOCK_AREAS, MOCK_CITIES } from '../../../lib/mockData-niche.js';

export interface CityRow {
  key: string;
  name_bg: string;
  name_en: string;
  name_lat: string | null;
  slug: string | null;
  aliases: string[];
  center_lat: number | null;
  center_lng: number | null;
  radius_m: number | null;
  active: boolean;
}

export interface CityAreaRow {
  id: number;
  city_key: string;
  name: string;
  name_lat: string;
  aliases: string[];
  include_in_queries: boolean;
  search_weight: number;
  source: string;
  has_polygon: boolean;
}

/** What the area form edits; `id` is null for a new row. */
export interface CityAreaInput {
  id: number | null;
  city_key: string;
  name: string;
  name_lat: string;
  aliases: string[];
  include_in_queries: boolean;
  search_weight: number;
}

const CITY_COLUMNS =
  'key, name_bg, name_en, name_lat, slug, aliases, center_lat, center_lng, radius_m, active';

export function useCities() {
  return useQuery({
    queryKey: ['cities'],
    queryFn: async (): Promise<CityRow[]> => {
      if (!isConfigured || isDemoMode) return MOCK_CITIES;
      const { data, error } = await supabase
        .from('cities')
        .select(CITY_COLUMNS)
        .order('active', { ascending: false })
        .order('name_bg');
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as CityRow[];
    },
  });
}

export function useCityAreas(cityKey: string | null) {
  return useQuery({
    queryKey: ['city_areas', cityKey],
    enabled: cityKey !== null,
    queryFn: async (): Promise<CityAreaRow[]> => {
      if (!isConfigured || isDemoMode) return MOCK_AREAS.filter((a) => a.city_key === cityKey);
      const { data, error } = await supabase
        .from('city_areas')
        .select(
          'id, city_key, name, name_lat, aliases, include_in_queries, search_weight, source, polygon',
        )
        .eq('city_key', cityKey!)
        .order('name');
      if (error) throw new Error(error.message);
      return (data ?? []).map((raw) => {
        const row = raw as unknown as Record<string, unknown>;
        return {
          id: row['id'] as number,
          city_key: row['city_key'] as string,
          name: row['name'] as string,
          name_lat: (row['name_lat'] as string | null) ?? '',
          aliases: (row['aliases'] as string[] | null) ?? [],
          include_in_queries: Boolean(row['include_in_queries']),
          search_weight: (row['search_weight'] as number | null) ?? 1,
          source: (row['source'] as string | null) ?? 'manual',
          has_polygon: row['polygon'] !== null && row['polygon'] !== undefined,
        };
      });
    },
  });
}

function invalidateCities(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['cities'] });
  void client.invalidateQueries({ queryKey: ['niche_context'] });
}

/** Insert when `isNew`, otherwise update by key (the key is the identity and never changes). */
export function useSaveCity() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ city, isNew }: { city: CityRow; isNew: boolean }) => {
      if (!isConfigured || isDemoMode) {
        const idx = MOCK_CITIES.findIndex((c) => c.key === city.key);
        if (idx === -1) MOCK_CITIES.push(city);
        else MOCK_CITIES[idx] = city;
        return;
      }
      const { key, ...rest } = city;
      const { error } = isNew
        ? await supabase.from('cities').insert({ key, ...rest })
        : await supabase.from('cities').update(rest).eq('key', key);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateCities(client),
  });
}

export function useSaveArea() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: CityAreaInput) => {
      const { id, ...rest } = input;
      if (!isConfigured || isDemoMode) {
        const idx = MOCK_AREAS.findIndex((a) => a.id === id);
        if (idx === -1)
          MOCK_AREAS.push({ ...rest, id: Date.now(), source: 'manual', has_polygon: false });
        else MOCK_AREAS[idx] = { ...MOCK_AREAS[idx]!, ...rest };
        return;
      }
      const { error } =
        id === null
          ? await supabase.from('city_areas').insert({ ...rest, source: 'manual' })
          : await supabase.from('city_areas').update(rest).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, input) =>
      void client.invalidateQueries({ queryKey: ['city_areas', input.city_key] }),
  });
}

export function useDeleteArea() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: number; city_key: string }) => {
      if (!isConfigured || isDemoMode) {
        const idx = MOCK_AREAS.findIndex((a) => a.id === id);
        if (idx !== -1) MOCK_AREAS.splice(idx, 1);
        return;
      }
      const { error } = await supabase.from('city_areas').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, input) =>
      void client.invalidateQueries({ queryKey: ['city_areas', input.city_key] }),
  });
}
